import React, { useState, RefObject } from 'react';
import { Check } from "lucide-react";
import { cn, formatPlaceName } from '@/lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/shared/command';
import { Input } from '@/components/shared/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/shared/popover';
import { useJurisdictionSearch } from '@/hooks/useJurisdictionSearch';
import { MIN_SEARCH_LENGTH } from '@/config/search';
import { Place } from '@/types';

export type { Place as PlaceOption }; // Legacy export for compatibility

interface JurisdictionComboboxProps {
  selectedPlace: Place | null;
  onSelect: (place: Place) => void;
  /**
   * Optional element to scroll into view after a manual selection.
   */
  scrollTargetRef?: RefObject<HTMLElement>;
}

export const JurisdictionCombobox: React.FC<JurisdictionComboboxProps> = ({ selectedPlace, onSelect, scrollTargetRef }) => {
  const [comboboxOpen, setComboboxOpen] = useState(false);

  const {
    searchInputValue,
    setSearchInputValue,
    placeOptions,
    isSearching,
    isDebouncing,
    searchError,
    clearResults,
  } = useJurisdictionSearch(comboboxOpen);

  // Popover size is handled internally by Radix; no extra tracking needed.

  const handleOpenChange = (isOpen: boolean) => {
    setComboboxOpen(isOpen);

    if (isOpen) {
      // When the combobox opens via click/focus, leave existing input intact.
      // Only reset if the field is currently empty (fresh session).
      if (searchInputValue.length === 0) {
        clearResults();
      }
    } else if (!isOpen && selectedPlace) {
      // Reset to last selection when closing without choosing
      setSearchInputValue(formatPlaceName(selectedPlace.name));
    }

    // no-op when closing beyond resetting search input
  };

  return (
    <Popover
      open={comboboxOpen}
      onOpenChange={handleOpenChange}
    >
      <PopoverTrigger asChild>
        <Input
          type="text"
          role="combobox"
          aria-expanded={comboboxOpen}
          placeholder={selectedPlace ? formatPlaceName(selectedPlace.name) : 'Search for a jurisdiction...'}
          value={searchInputValue}
          onChange={(e) => {
            const val = e.target.value;
            // Auto-open when there are enough characters; close otherwise.
            if (val.length >= MIN_SEARCH_LENGTH && !comboboxOpen) {
              setComboboxOpen(true);
            } else if (val.length < MIN_SEARCH_LENGTH && comboboxOpen) {
              setComboboxOpen(false);
            }
            setSearchInputValue(val);
          }}
          onPointerDown={(e) => {
            // Stop the click from toggling Radix's PopoverTrigger behavior.
            e.stopPropagation();
          }}
          className="w-full bg-white border rounded-md px-3 py-2 text-sm text-black focus-visible:ring-0 focus-visible:ring-offset-0"
        />
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] max-h-[--radix-popover-content-available-height] p-0 bg-white"
        sideOffset={8}
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Command shouldFilter={false}>
          <CommandList>
            {isSearching ? (
              <div className="p-2 text-center text-sm">Loading...</div>
            ) : (
              <>
                {searchError && (
                  <div className="p-2 text-center text-sm text-red-600">{searchError}</div>
                )}

                {searchInputValue.length >= MIN_SEARCH_LENGTH && (
                  <CommandEmpty>
                    {isDebouncing ? 'Searching...' : 'No results found.'}
                  </CommandEmpty>
                )}

                <CommandGroup>
                  {placeOptions.map((place) => (
                    <CommandItem
                      key={`${place.name}-${place.place_fips}`}
                      value={place.name}
                      onSelect={() => {
                        console.log('[JurisdictionCombobox] Selected place:', place, 'place_fips:', place.place_fips);
                        onSelect(place);
                        setSearchInputValue(formatPlaceName(place.name));
                        setComboboxOpen(false);

                        // Smooth-scroll the provided container into view, if any.
                        if (scrollTargetRef?.current) {
                          setTimeout(() => {
                            scrollTargetRef.current?.scrollIntoView({ behavior: 'smooth' });
                          }, 300);
                        }
                      }}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          selectedPlace?.place_fips === place.place_fips
                            ? 'opacity-100'
                            : 'opacity-0'
                        )}
                      />
                      {formatPlaceName(place.name)}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default JurisdictionCombobox; 